import type { DataRouter, RouterState } from 'react-router';
import { track } from './client';

/**
 * Page-view tracking.
 *
 * Raw URLs are never sent. Several routes embed capability tokens
 * (`/employee-consent/:linkToken`, `/guarantor-recommendation/:token`) — anyone
 * holding one can open an employee's consent form, so leaking them into an
 * analytics vendor would be a genuine disclosure. Paths are reduced to their
 * route pattern before anything leaves the browser.
 */

/** Query params that are safe and useful to keep for attribution. */
const ALLOWED_QUERY_PARAMS = ['ref', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

interface RouteMatchLike {
    route?: { path?: string; index?: boolean };
}

/**
 * Rebuilds the route pattern (e.g. `/investigations/:id/report`) from the
 * matched route chain, so IDs and tokens are replaced by their parameter names.
 */
export const toRoutePattern = (matches: readonly RouteMatchLike[]): string => {
    const segments: string[] = [];

    for (const match of matches) {
        const path = match.route?.path;
        if (!path || path === '/') continue;
        segments.push(path.replace(/^\/+|\/+$/g, ''));
    }

    const pattern = segments.filter(Boolean).join('/');
    return pattern ? `/${pattern}` : '/';
};

/**
 * Turns a route pattern into the page slug used in the event name.
 *
 *   /                            → home
 *   /dashboard                   → dashboard
 *   /hr/dashboard                → hr_dashboard
 *   /investigations              → investigations
 *   /investigations/:id          → investigations_detail
 *   /investigations/:id/report   → investigations_report
 *   /employee-consent/:linkToken → employee_consent_detail
 *
 * Param segments are dropped rather than interpolated — they hold IDs and
 * consent tokens. A pattern that *ends* in a param gets `_detail` appended so
 * a list route and its detail route don't collapse onto the same name.
 */
export const toPageSlug = (pattern: string): string => {
    const segments = pattern.split('/').filter(Boolean);
    if (segments.length === 0) return 'home';

    const isParam = (segment: string) => segment.startsWith(':');
    const statics = segments.filter((segment) => !isParam(segment)).map((segment) => segment.replace(/-/g, '_'));

    if (statics.length === 0) return 'home';

    const slug = statics.join('_');
    const lastSegment = segments[segments.length - 1];
    return lastSegment && isParam(lastSegment) ? `${slug}_detail` : slug;
};

/** Event name for a page view, e.g. `dashboard:page_viewed`. */
export const toPageViewEvent = (pattern: string): string => `${toPageSlug(pattern)}:page_viewed`;

/**
 * Last reported pattern. Module-level rather than per-subscription so that
 * React 19 StrictMode — which mounts, unmounts and remounts effects in dev —
 * doesn't emit a page view twice for the entry page.
 */
let lastPattern: string | null = null;

/** Keeps only allowlisted attribution params. */
const safeQueryProps = (search: string): Record<string, string> => {
    const props: Record<string, string> = {};
    if (!search) return props;

    const params = new URLSearchParams(search);
    for (const key of ALLOWED_QUERY_PARAMS) {
        const value = params.get(key);
        if (value) props[key] = value;
    }
    return props;
};

/**
 * Subscribes to router navigation and emits a per-page view event
 * (`dashboard:page_viewed`, `investigations_detail:page_viewed`, …).
 *
 * Returns an unsubscribe function. Fires once immediately for the entry page,
 * which `router.subscribe` alone would miss.
 */
export const initRouteTracking = (router: DataRouter): (() => void) => {
    const report = (state: RouterState) => {
        const pattern = toRoutePattern(state.matches);

        // React Router emits several state updates per navigation (loading →
        // idle). Only report when the resolved pattern actually changes.
        if (pattern === lastPattern) return;
        lastPattern = pattern;

        // `path` and `page` are kept alongside the per-page event name so a
        // single report can still union every page view via the `page_viewed`
        // suffix, or break down by path without listing each event by hand.
        track(toPageViewEvent(pattern), {
            page: toPageSlug(pattern),
            path: pattern,
            // Depth is a cheap proxy for how deep in the product a session got.
            depth: pattern.split('/').filter(Boolean).length,
            ...safeQueryProps(state.location.search),
        });
    };

    report(router.state);
    return router.subscribe((state) => {
        if (state.navigation.state === 'idle') report(state);
    });
};
